import {
  createExerciseFn,
  deleteExerciseFn,
  listAllExercisesOptions,
  toggleExerciseActiveFn,
  updateExerciseFn,
} from "@/actions/exercise/handler";
import { getMuscleGroupName } from "@/api/db/schema/exercises";
import type { SelectExercise } from "@/api/db/schema/exercises";
import { ExerciseForm } from "@/components/admin/ExerciseForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Edit,
  MoreVertical,
  Plus,
  Power,
  PowerOff,
  Search,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/exercises")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listAllExercisesOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useSuspenseQuery(listAllExercisesOptions());
  const { queryClient } = useRouteContext({ from: "/_app/admin/exercises" });

  const [searchQuery, setSearchQuery] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<SelectExercise | null>(
    null,
  );
  const [deleteExercise, setDeleteExercise] = useState<SelectExercise | null>(
    null,
  );

  const rawExercises = data.body?.exercises ?? [];
  const exercises = rawExercises.map((ex) => ({
    ...ex,
    aliases: ex.aliases ?? [],
  }));

  const filteredExercises = exercises.filter(
    (ex) =>
      ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ex.aliases || []).some((alias) =>
        alias.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  // Mutations
  const createMutation = useMutation({
    mutationFn: createExerciseFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllExercisesOptions().queryKey,
      });
      toast.success("Egzersiz oluşturuldu");
      setIsFormOpen(false);
      setEditingExercise(null);
    },
    onError: (error) => {
      toast.error("Hata", { description: error.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateExerciseFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllExercisesOptions().queryKey,
      });
      toast.success("Egzersiz güncellendi");
      setIsFormOpen(false);
      setEditingExercise(null);
    },
    onError: (error) => {
      toast.error("Hata", { description: error.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExerciseFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllExercisesOptions().queryKey,
      });
      toast.success("Egzersiz silindi");
      setDeleteExercise(null);
    },
    onError: (error) => {
      toast.error("Hata", { description: error.message });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleExerciseActiveFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllExercisesOptions().queryKey,
      });
      toast.success(response.body?.message);
    },
    onError: (error) => {
      toast.error("Hata", { description: error.message });
    },
  });

  // Handlers
  const handleEdit = (exercise: SelectExercise) => {
    // Ensure aliases is array
    const safeExercise = {
      ...exercise,
      aliases: exercise.aliases || [],
    };
    setEditingExercise(safeExercise);
    setIsFormOpen(true);
  };

  const handleDelete = (exercise: SelectExercise) => {
    setDeleteExercise(exercise);
  };

  const handleToggleActive = (exercise: SelectExercise) => {
    toggleActiveMutation.mutate({ data: { exerciseId: exercise.exerciseId } });
  };

  const handleFormSubmit = async (formData: any) => {
    if (editingExercise) {
      await updateMutation.mutateAsync({
        data: {
          exerciseId: editingExercise.exerciseId,
          ...formData,
        },
      });
    } else {
      await createMutation.mutateAsync({ data: formData });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteExercise) {
      deleteMutation.mutate({
        data: { exerciseId: deleteExercise.exerciseId },
      });
    }
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-bold text-2xl sm:text-3xl">Egzersiz Yönetimi</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Egzersiz kütüphanesini yönetin ({exercises.length} egzersiz)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setEditingExercise(null);
              setIsFormOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Yeni Egzersiz
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Egzersiz ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Exercise Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredExercises.map((exercise) => (
          <Card
            key={exercise.exerciseId}
            className={`transition-opacity ${!exercise.isActive ? "opacity-60" : ""}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base">{exercise.name}</CardTitle>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">
                      {getMuscleGroupName(exercise.muscleGroup)}
                    </Badge>
                    {exercise.videoUrl && (
                      <Badge
                        variant="outline"
                        className="text-blue-500 text-xs"
                      >
                        Video
                      </Badge>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(exercise)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Düzenle
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleToggleActive(exercise)}
                    >
                      {exercise.isActive ? (
                        <>
                          <PowerOff className="mr-2 h-4 w-4" />
                          Devre Dışı Bırak
                        </>
                      ) : (
                        <>
                          <Power className="mr-2 h-4 w-4" />
                          Aktifleştir
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleDelete(exercise)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              {(exercise.aliases || []).length > 0 && (
                <div className="flex flex-wrap gap-1 text-muted-foreground text-xs">
                  {(exercise.aliases || []).map((alias) => (
                    <span key={alias} className="rounded bg-muted px-1">
                      #{alias}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {filteredExercises.length === 0 && (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            Egzersiz bulunamadı
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExercise ? "Egzersizi Düzenle" : "Yeni Egzersiz"}
            </DialogTitle>
          </DialogHeader>
          <ExerciseForm
            initialData={editingExercise || undefined}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingExercise(null);
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteExercise}
        onOpenChange={(open) => !open && setDeleteExercise(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Egzersizi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteExercise?.name}" egzersizini silmek istediğinize emin
              misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
